import { CommentsIcon } from "@/assets/icons/comments";
import { FbLikeIcon } from "@/assets/icons/fb-like";
import { FbLoveIcon } from "@/assets/icons/fb-love";
import { ShareIcon } from "@/assets/icons/share";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { PulseTimelapseProps } from "../../types/pulse.types";

export default function PulseTimelapse({
  postData,
  onShowJSON,
}: PulseTimelapseProps) {
  // Show skeleton loading state if no postData yet
  if (!postData) {
    return (
      <Card className="flex w-full flex-col !gap-3 !p-4 !shadow-none">
        <CardHeader>
          <CardTitle className="!leading-9 !-tracking-wide">
            Post Details
          </CardTitle>
        </CardHeader>
        <div className="flex w-full flex-col gap-4 py-3">
          <div className="flex flex-row gap-2">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-4 h-20 w-full" />
          <div className="mt-4 flex gap-3">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-16" />
          </div>
          <Skeleton className="mt-4 h-10 w-full" />
        </div>
      </Card>
    );
  }

  const getPlatformIcon = () => {
    if (postData.soc_source === "facebook") {
      return (
        <img src="/logo/fb-logo.svg" alt="Facebook" width={20} height={20} />
      );
    }
    if (postData.soc_source === "twitter") {
      return (
        <img src="/logo/x-logo.svg" alt="Twitter" width={20} height={20} />
      );
    }
    if (postData.soc_source === "instagram") {
      return (
        <img src="/logo/ig-logo.svg" alt="Instagram" width={20} height={20} />
      );
    }
    if (postData.soc_source === "youtube") {
      return (
        <img src="/logo/yt-logo.svg" alt="YouTube" width={20} height={20} />
      );
    }
    if (postData.soc_source === "tiktok") {
      return (
        <img src="/logo/tiktok-logo.svg" alt="TikTok" width={20} height={20} />
      );
    }
    return null;
  };

  const getChannelName = () => {
    if (
      postData.article_url?.includes("x.com/") ||
      postData.article_url?.includes("twitter.com/")
    ) {
      const match = postData.article_url.match(
        /(?:x\.com|twitter\.com)\/([^/]+)/,
      );
      return match ? `@${match[1]}` : postData.soc_source || "Unknown Channel";
    }
    return postData.soc_source || "Unknown Channel";
  };

  const getPostId = () => {
    return postData.soc_post_id || "N/A";
  };

  const getPublishedDate = () => {
    const dateStr = postData.date_publish || postData.date_created;
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  const getContent = () => {
    return postData.content || postData.title || "No content available";
  };

  const getStats = () => {
    if (!postData.engagements) {
      return { likes: 0, comments: 0, shares: 0, views: 0 };
    }
    const eng = postData.engagements;
    return {
      likes:
        (eng.soc_like_count || 0) +
        (eng.soc_love_count || 0) +
        (eng.soc_wow_count || 0) +
        (eng.soc_haha_count || 0),
      comments: eng.soc_comment_count || 0,
      shares: eng.soc_share_count || 0,
      views: eng.soc_view_count || 0,
    };
  };

  const stats = getStats();

  return (
    <Card className="flex w-full flex-col !gap-3 !p-4 !shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="!leading-9 !-tracking-wide">
            Post Details
          </CardTitle>
        </div>
      </CardHeader>

      <div className="flex w-full flex-col gap-2 py-3 pb-0">
        <div className="flex flex-row justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex flex-row items-center gap-1">
              {getPlatformIcon()}
              <p className="font-sm leading-normal !font-semibold !text-[#141413]">
                {getChannelName()}
              </p>
            </div>
            <p className="!text-sm !leading-[140%] !font-semibold !tracking-normal !text-neutral-600">
              ID: {getPostId()}
            </p>
            <p className="!text-xs !leading-[140%] !font-medium !text-neutral-600">
              Posted {getPublishedDate()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4">
        <div className="h-auto w-full">
          <p className="!text-xs leading-normal !font-medium break-words whitespace-pre-wrap !text-neutral-950">
            {getContent()}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-row flex-wrap items-center gap-3">
            <div className="flex flex-row items-center gap-1.5">
              <FbLikeIcon width={18} height={18} className="z-10" />
              <FbLoveIcon
                width={18}
                height={18}
                className="z-20 -ml-3 rounded-full"
              />
              <p className="!text-xs !leading-[140%] !font-bold !text-neutral-600">
                {stats.likes.toLocaleString()}
              </p>
            </div>
            <div className="flex flex-row items-center gap-1.5">
              <CommentsIcon width={18} height={18} className="z-10" />
              <p className="!text-xs !leading-[140%] !font-bold !text-neutral-600">
                {stats.comments.toLocaleString()}
              </p>
            </div>
            <div className="flex flex-row items-center gap-1.5">
              <ShareIcon width={18} height={18} className="z-10" />
              <p className="!text-xs !leading-[140%] !font-bold !text-neutral-600">
                {stats.shares.toLocaleString()}
              </p>
            </div>
            {stats.views > 0 && (
              <div className="flex flex-row items-center gap-1.5">
                <i className="ri-eye-line text-[18px] text-neutral-800"></i>
                <p className="!text-xs !leading-[140%] !font-bold !text-neutral-600">
                  {stats.views.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          <Button
            variant="tertiary"
            className="!w-full !text-xs !leading-normal"
            onClick={onShowJSON}
          >
            Show channel JSON
          </Button>
        </div>
      </div>
    </Card>
  );
}
